import React, { useContext, useEffect } from 'react'
import { Head } from '@notino/react-toolkit/exports/Head'
import { useIntl } from 'react-intl'

// utils
import { DEFAULT_LOCALE, getEnvironmentAndLocaleSpecificShopsConfig, getEnvironmentSpecificShopsConfig } from '../../../../appDefaults'
import { PAGE_LINKS } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

// types
import { CategoriesSeoSlugResponse, CitiesSeoSlugResponse, CmsSalonsMetadata, SalonsPagination } from '../../../../types/types'

type Props = {
	pagination: SalonsPagination
	primaryCategorySlug: string
	primaryCitySlug: string | undefined
	categoryID: string
	metaData: CmsSalonsMetadata
	categoriesSeoSlugData: CategoriesSeoSlugResponse | undefined
	citiesSeoSlugData: CitiesSeoSlugResponse | undefined
}

type HrefLang = { href: string; lang: string }

const SalonsCategoryPageHead = (props: Props) => {
	const { locale } = useIntl()
	const { productionEnvironment } = useContext(AppContext)

	const { pagination, primaryCategorySlug, primaryCitySlug, categoryID, categoriesSeoSlugData, citiesSeoSlugData, metaData } = props

	const defaultShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, DEFAULT_LOCALE)
	const currentShop = getEnvironmentAndLocaleSpecificShopsConfig(productionEnvironment, locale)
	const shops = getEnvironmentSpecificShopsConfig(productionEnvironment)

	const pageTitle = metaData.metaTitle

	let canonicalHref: string
	let currentPath: string
	let alternateLinks: HrefLang[] = []
	let xDefaultHref: string | undefined

	useEffect(() => {
		document.title = pageTitle
	}, [pageTitle])

	const defaultCategorySlug = categoriesSeoSlugData?.category?.primarySeoSlugsByCountry?.[defaultShop.countryCode.toLowerCase()] || categoryID

	if (citiesSeoSlugData && primaryCitySlug) {
		currentPath = `${currentShop.url}${PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](locale, primaryCategorySlug, primaryCitySlug)}`
		canonicalHref = currentPath

		alternateLinks = shops.reduce<HrefLang[]>((acc, shop) => {
			const citySlug = citiesSeoSlugData.primarySeoSlugsByCountry[shop.countryCode.toLowerCase()]

			if (!citySlug) {
				return acc
			}

			const categorySlug = categoriesSeoSlugData?.category?.primarySeoSlugsByCountry?.[shop.countryCode.toLowerCase()] || categoryID

			acc.push({
				href: `${shop.url}${PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](shop.locale, categorySlug, citySlug)}`,
				lang: shop.lang
			})
			return acc
		}, [])

		if (alternateLinks.length === 1 && alternateLinks[0].href === canonicalHref) {
			// We avoid outputting a single rel="alternate" that only points to the canonical URL.
			// A lone self-referential alternate adds no value and can create unnecessary noise for search engines.
			alternateLinks = []
		}

		const defaultCitySlug = citiesSeoSlugData.primarySeoSlugsByCountry[defaultShop.countryCode.toLowerCase()]

		if (defaultCitySlug && alternateLinks.length) {
			// Only set x-default when we actually have alternate variants (otherwise x-default is meaningless)
			xDefaultHref = `${defaultShop.url}${PAGE_LINKS['/salons/all/:categorySlug/:citySlug'](defaultShop.locale, defaultCategorySlug, defaultCitySlug)}`
		}
	} else {
		currentPath = `${currentShop.url}${PAGE_LINKS['/salons/all/:categorySlug'](locale, primaryCategorySlug)}`
		canonicalHref = currentPath

		alternateLinks = shops.reduce<HrefLang[]>((acc, shop) => {
			const categorySlug = categoriesSeoSlugData?.category?.primarySeoSlugsByCountry[shop.countryCode.toLowerCase()] || categoryID

			acc.push({
				href: `${shop.url}${PAGE_LINKS['/salons/all/:categorySlug'](shop.locale, categorySlug)}`,
				lang: shop.lang
			})

			return acc
		}, [])

		if (alternateLinks.length === 1 && alternateLinks[0].href === canonicalHref) {
			// Same rationale as above: suppress a solitary self-referential alternate link.
			alternateLinks = []
		}

		if (alternateLinks.length) {
			// Only set x-default when we actually have alternate variants (otherwise x-default is meaningless)
			xDefaultHref = `${defaultShop.url}${PAGE_LINKS['/salons/all/:categorySlug'](defaultShop.locale, defaultCategorySlug)}`
		}
	}

	// prev link
	const hasLessPages = pagination && pagination.page !== 1

	let prevLink: string | undefined
	if (hasLessPages) {
		prevLink = `${currentPath}?page=${pagination.page - 1}`
	}

	// next link
	const hasMorePages = pagination && pagination.totalPages && pagination.page !== pagination.totalPages

	let nextLink: string | undefined
	if (hasMorePages) {
		nextLink = `${currentPath}?page=${pagination.page + 1}`
	}

	// NOTE: defining children as array and using .flat() on it otherwise <Head> doesn't work properly
	// each item has to have unique 'key' because <Head/> iterates over the array
	const children = [
		// charset
		<meta key='charset' charSet='utf-8' />,

		// title
		<title key='title'>{pageTitle}</title>,

		// description
		<meta key='description' name='description' content={metaData.metaDescription} />,

		// keywords
		<meta key='keywords' name='keywords' content={metaData.metaKeywords} />,

		// Indexing
		<meta key='robots' name='robots' content={metaData.indexPage ? 'index, follow' : 'noindex, nofollow'} />,

		// canonical
		canonicalHref && <link key='canonical' rel='canonical' href={canonicalHref} />,
		// alternates
		alternateLinks.map((link, id) => {
			return <link key={`alternate-${id}`} rel='alternate' href={link.href} hrefLang={link.lang} />
		}),
		// x-default
		xDefaultHref && <link key='x-default' rel='alternate' href={xDefaultHref} hrefLang='x-default' />,

		// pagination SEO links
		prevLink && <link key='prev' rel='prev' href={prevLink} />,
		nextLink && <link key='next' rel='next' href={nextLink} />
	]
		.flat()
		.filter(Boolean)

	return <Head>{children}</Head>
}

export default SalonsCategoryPageHead
